import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const VolumeChart = ({ data, loading = false, className = '' }) => {
  if (loading) {
    return (
      <div className={`volume-chart loading ${className}`}>
        <div className="chart-skeleton"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className={`volume-chart empty ${className}`}>
        <p>No chart data available</p>
      </div>
    );
  }

  // Convert mutez to tez and format data for Chart.js
  const chartData = {
    labels: data.map((item, index) => {
      if (item.date) {
        return new Date(item.date).toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        });
      } else if (item.timestamp) {
        return new Date(item.timestamp).toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        });
      } else {
        return `Day ${index + 1}`;
      }
    }),
    datasets: [
      {
        label: 'Volume (XTZ)',
        data: data.map(item => (item.value || 0) / 1000000), // Convert mutez to tez
        borderColor: '#e00fa8',
        backgroundColor: 'rgba(224, 15, 168, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#e00fa8',
        pointBorderColor: '#e00fa8',
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#e00fa8',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            if (value >= 1000000) {
              const formatted = (value / 1000000).toFixed(1);
              return `Volume: ${formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted}M XTZ`;
            } else if (value >= 1000) {
              const formatted = (value / 1000).toFixed(1);
              return `Volume: ${formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted}K XTZ`;
            } else {
              const formatted = value.toFixed(1);
              return `Volume: ${formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted} XTZ`;
            }
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
          drawBorder: false,
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            family: 'var(--font-pixel-secondary)',
            size: 10,
          },
          maxTicksLimit: 8,
        },
      },
      y: {
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
          drawBorder: false,
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            family: 'var(--font-pixel-secondary)',
            size: 10,
          },
          callback: function(value) {
            if (value >= 1000000) {
              const formatted = (value / 1000000).toFixed(1);
              return formatted.endsWith('.0') ? formatted.slice(0, -2) + 'M' : formatted + 'M';
            } else if (value >= 1000) {
              const formatted = (value / 1000).toFixed(1);
              return formatted.endsWith('.0') ? formatted.slice(0, -2) + 'K' : formatted + 'K';
            } else {
              const formatted = value.toFixed(1);
              return formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted;
            }
          }
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
  };

  return (
    <div className={`volume-chart ${className}`}>
      <Line data={chartData} options={options} />
    </div>
  );
};

export default VolumeChart;
